import type { CatalogOf } from '../types';
import type { en } from './en';

export const zh: CatalogOf<typeof en> = {
  common: {
    save: '保存',
    cancel: '取消',
    apply: '应用',
    close: '关闭',
    back: '返回',
    verify: '验证',
    loading: '加载中...',
    retry: '重试',
    or: '或',
    today: '今天',
    doctor: '医生',
    clinic: '诊所',
    unknown: '未知',
    copy: '复制',
    copied: '已复制!',
    add: '添加',
  },
  language: {
    label: '界面语言',
    english: 'English',
    indonesian: 'Bahasa Indonesia',
  },
  header: {
    sessions: '就诊',
    back: '返回就诊列表',
    logout: '退出登录',
    history: '历史',
    chatWithEhr: '与 EHR 对话',
    dashboard: '仪表板',
    clinic: '诊所',
    aiUsage: 'AI 用量',
    profile: '个人资料',
    endSession: '结束就诊',
    stopViewing: '停止查看',
    actingAs: '当前身份：{name} · {clinic}',
    impersonation: '（模拟）',
    viewingClinic: '正在查看：{clinic}',
    readOnly: '（只读）',
    logoAlt: 'AIGP Health 标志',
    pages: {
      consultTitle: '患者问诊',
      consultSubtitle: 'AI 记录与临床文档',
      dashboardTitle: '医生仪表板',
      dashboardSubtitle: '欢迎回来，{name}',
      sessionsTitle: '管理就诊',
      sessionsSubtitle: '创建并管理患者咨询就诊',
      meetingTitle: '在线咨询',
      meetingSubtitle: '患者咨询的虚拟会议室',
      profileTitle: '我的资料',
      profileSubtitle: '管理您的账户信息和偏好',
      clinicProfileTitle: '诊所资料',
      clinicProfileSubtitle: '管理诊所信息和设置',
      clinicAdminTitle: '诊所管理',
      clinicAdminSubtitle: '欢迎，{name}',
      adminTitle: '管理员仪表板',
      adminSubtitle: '欢迎回来，{name}',
      superAdminTitle: '超级管理员仪表板',
      superAdminSubtitle: '欢迎，{name}',
      aiUsageTitle: 'AI 用量',
      aiUsageSubtitle: '各 AI 流程的令牌消耗、费用与延迟',
      defaultTitle: 'AIGP Health',
      defaultSubtitle: '医疗咨询平台',
      adminFallback: '管理员',
      superAdminFallback: '超级管理员',
    },
  },
  auth: {
    welcomeBack: '欢迎回来',
    loginSubtitle: '登录以继续使用您的 AIGP Health 工作区。',
    signInEyebrow: '登录',
    signInTitle: '登录您的账户',
    email: '电子邮箱',
    emailPlaceholder: 'you@clinic.com',
    password: '密码',
    passwordPlaceholder: '输入您的密码',
    showPassword: '显示密码',
    hidePassword: '隐藏密码',
    signIn: '登录',
    signInGoogle: '使用 Google 登录',
    forgotPassword: '忘记密码？',
    createAccount: '创建账户',
    verifyEyebrow: '验证身份',
    enterCode: '输入验证码',
    otpHint: '输入通过 WhatsApp 发送到您登记手机的 6 位验证码。',
    loginOtpAria: '登录验证码',
    resendIn: '{seconds} 秒后可重新发送',
    requestNewCode: '重新获取验证码',
    secureEyebrow: '保护您的账户',
    mfaTitle: '设置双因素认证',
    mfaHint: '添加手机号以通过 WhatsApp 接收一次性验证码。',
    phone: '手机号码',
    phonePlaceholder: '输入手机号码',
    sendCode: '发送验证码',
    resendCode: '重新发送',
    mfaOtpAria: 'MFA 设置验证码',
    invalidEmail: '请输入有效的电子邮箱。',
    enterPassword: '请输入您的密码。',
    emailUnverified: '您的邮箱尚未验证。设置 MFA 前请先完成验证。',
    authError: '认证错误。请联系支持。',
    badCredentials: '邮箱或密码不正确。请重试。',
    unableToSignIn: '无法登录。请重试。',
    enterFullCode: '请输入完整的 6 位验证码。',
    sessionExpired: '会话已过期。请重新登录。',
    invalidCode: '验证码无效。请重试。',
    couldNotResend: '无法重新发送验证码。',
    startOver: '重新开始',
    attemptRemaining: '剩余 {count} 次尝试',
    attemptsRemaining: '剩余 {count} 次尝试',
    invalidPhone: '请输入有效的手机号码。',
    couldNotSend: '无法发送验证码。',
    googleUnexpected: 'Google 登录返回了意外响应。',
    googleFailed: 'Google 登录失败。',
    routingFailed: '登录成功但跳转失败。请重试。',
    shellDefaultTitle: '欢迎使用 AIGP Health',
    shellDefaultSubtitle: '面向风湿科与全科的循证临床决策支持。',
    trustHipaa: '符合 HIPAA 的数据处理',
    trustEncrypted: '传输与存储均加密',
    trustEvidence: '经临床医生核实的证据',
    registrationPhases: '注册阶段',
    trustAria: '信任与安全',
    progress: '进度',
    disclaimerTitle: '免责声明',
    disclaimerBody:
      'AIGP Health 及其 AI 平台“Anzu”旨在协助——而非取代——合格医疗专业人员的临床判断。所有临床决策必须由持证医生做出。Anzu 提供的信息不能替代专业医疗建议、诊断或治疗。',
    iUnderstand: '我已了解',
  },
  forgot: {
    title: '重置密码',
    subtitle: '我们将帮您重新进入账户。',
    eyebrow: '账户恢复',
    stepEmail: '重置密码',
    stepOtp: '验证验证码',
    stepReset: '设置新密码',
    hintEmail: '输入您的邮箱，我们将发送重置验证码。',
    hintOtp: '输入发送到您手机的 6 位验证码。',
    hintReset: '输入邮件中的令牌并设置新密码。',
    sendResetCode: '发送重置验证码',
    otpAria: '密码重置验证码',
    resetToken: '重置令牌',
    tokenPlaceholder: '粘贴邮件中的令牌',
    newPassword: '新密码',
    newPasswordPlaceholder: '输入新密码',
    confirmPassword: '确认新密码',
    confirmPlaceholder: '再次输入新密码',
    resetPassword: '重置密码',
    backToSignIn: '返回登录',
    codeSent: '重置验证码已发送到您的手机。',
    codeResent: '已发送新的重置验证码。',
    sendFailed: '发送验证码失败。请重试。',
    resendFailed: '重新发送验证码失败。',
    enterFullCode: '请输入完整的 6 位验证码。',
    tokenSent: '重置令牌已发送到您的邮箱。请在下方与新密码一起输入。',
    invalidCode: '验证码无效。请重试。',
    enterToken: '请输入邮件中的重置令牌。',
    passwordRequirements: '密码不符合要求。',
    passwordsMismatch: '两次输入的密码不一致。',
    resetSuccess: '密码已重置。正在跳转到登录…',
    resetFailed: '重置密码失败。请重试。',
  },
  dashboard: {
    approvalTitle: '诊所审批待处理',
    approvalBody: '您的诊所注册正在由管理团队审核。',
    approvalAccess: '诊所获批后即可访问医生门户。',
    approvalHelp: '如有疑问，请联系诊所管理员或支持。',
    welcome: '您好，{name} 医生！',
    welcomeBody: '准备好管理患者就诊并提供优质护理',
    whatToDo: '您想做什么？',
    manageSessions: '管理就诊',
    manageSessionsBody: '创建、编辑并整理患者咨询就诊',
    manageFeature1: '安排新咨询',
    manageFeature2: '更新患者信息',
    manageFeature3: '管理预约详情',
    clinicAdmin: '诊所管理',
    clinicAdminBody: '管理用户、订阅和诊所概览',
    clinicFeature1: '概览与分析',
    clinicFeature2: '用户管理',
    clinicFeature3: '订阅管理',
  },
  usage: {
    loadingChip: '就诊…',
    loadingBanner: '正在加载您的就诊用量…',
    loadingTitle: '正在加载就诊用量',
    errorChip: '就诊 —',
    errorBanner: '无法加载就诊用量。{error}',
    loadFailed: '加载就诊用量失败',
    unlimitedTitle: '不限就诊次数 — 无月度上限',
    usedTitle: '本月已使用 {used} / {limit} 次就诊',
    unlimitedChip: '就诊 ∞',
    sessionsLabel: '次就诊',
    unlimitedBanner: '不限次数 — 无月度上限。',
    usedThisPeriod: '本周期您已使用 {used} 次就诊。',
    usedThisPeriodPlural: '本周期您已使用 {used} 次就诊。',
    reachedLimit: '您已达到本月就诊上限（{used}/{limit}）。下个周期将重置。',
    oneLeft: '本月还剩 1 次就诊。已用 {used}/{limit}。',
    manyLeft: '本月还剩 {remaining} 次就诊。已用 {used}/{limit}。',
  },
  inactivity: {
    title: '会话即将过期',
    body: '由于长时间未操作，您的会话将在以下时间后过期：',
    continueQuestion: '是否继续当前会话？',
    logoutNow: '立即退出',
    continueSession: '继续会话',
  },
  subscription: {
    title: '订阅待处理',
    bodyNamed: '{name} 医生，您的账户已就绪，但诊所尚未开通有效订阅。',
    bodyUnnamed: '您的账户已就绪，但诊所尚未开通有效订阅。',
    access: '诊所开通套餐后即可访问医生门户。',
    help: '只有诊所管理员可以开通订阅。开通后请在下方重新检查或重新登录。',
    checking: '正在检查…',
    checkAgain: '重新检查',
    stillPending: '您的诊所仍没有有效订阅。请联系诊所管理员。',
    checkFailed: '无法检查订阅状态。请稍后再试。',
  },
  sessions: {
    tabSessions: '就诊',
    tabSignature: '签名',
    tabMedicationCatalog: '药品目录',
    tabProtocols: '诊疗路径',
    tabEducation: '宣教内容',
    tabScheduling: '排班',
    noSignature: '尚未设置签名',
    filterToday: '今天',
    filterTomorrow: '明天',
    filterThisWeek: '本周',
    filterAll: '全部',
    filterCustom: '自定义范围',
    listView: '列表视图',
    calendarView: '日历视图',
    allDoctors: '全部医生',
    sortBy: '排序：',
    sortDefault: '默认（最新优先）',
    sortDateNewest: '预约日期 — 最新优先',
    sortDateOldest: '预约日期 — 最早优先',
    sortTimeEarliest: '预约时间 — 最早优先',
    sortTimeLatest: '预约时间 — 最晚优先',
    sortIdLow: '就诊编号 — 从低到高',
    sortIdHigh: '就诊编号 — 从高到低',
    searchPlaceholder: '按患者姓名或电话搜索...',
    bulkUpload: '批量上传',
    createNew: '创建新就诊',
    loading: '正在加载就诊...',
    appointmentsHeading: '{filter}预约',
    appointmentsRange: '{start} - {end} 预约',
    emptyTitle: '未找到{filter}预约',
    emptySearch: '没有符合搜索条件的{filter}预约。',
    emptyScheduled: '{filter}没有已安排的预约。',
    emptyRange: '未找到 {start} - {end} 的预约',
    emptyRangeSearch: '所选日期范围内没有符合搜索条件的预约。',
    emptyAll: '暂无就诊。请创建第一个就诊以开始。',
    emptyRangeScheduled: '所选日期范围内没有已安排的预约。',
    emptyPickRange: '请选择日期范围以查看预约。',
    showingRange: '显示 {start}-{end} / 共 {total}',
    colPatient: '患者姓名',
    colPatientHint: '使用搜索查找患者',
    colPhone: '电话号码',
    colDoctor: '医生',
    colNotification: '通知',
    colPreconsult: '诊前',
    colType: '就诊类型',
    sent: '已发送',
    notSent: '未发送',
    rowsPerPage: '每页行数：',
    statusNotArrived: '未到达',
    statusCheckedIn: '已签到',
    statusInConsultation: '问诊中',
    statusCompleted: '就诊已完成',
    statusCompletedShort: '已完成',
    statusNoShow: '未到诊',
    statusCancelled: '已取消',
    createTitle: '创建新就诊',
    searchMrn: '按病历号搜索',
    searchMrnPlaceholder: '输入病历号后 4 位及以上进行搜索',
    searching: '正在搜索...',
    patientName: '患者姓名 *',
    patientNamePlaceholder: '输入患者全名',
    phone: '电话号码 *',
    phonePlaceholder: '输入电话号码',
    mrnOptional: '病历号（可选）',
    mrnPlaceholder: '如有诊所病历号请输入',
    dob: '出生日期 *',
    dobRequired: '必须填写出生日期',
    dobMissingOnRecord: '此患者记录没有出生日期。请填写后再创建就诊。',
    patientMatchHint: '输入姓名至少两个字符即可查看现有患者。',
    matchingPatients: '匹配的现有患者',
    sameNameWarning: '请核对：多条记录使用此姓名。选择前请用电话尾号或出生日期确认。',
    noneOfTheseNewPatient: '都不是 — 作为新患者继续',
    sendLinkHint: '发送问诊前链接',
    resendLinkHint: '发送新的问诊前链接',
    sendLinkTitle: '发送问诊前链接？',
    resendLinkTitle: '发送新的问诊前链接？',
    resendLinkBody: '将通过 WhatsApp 向 {name}（{phone}）发送问诊前链接。',
    resendLinkSupersedes: '新链接发出后，之前发送的链接将立即失效。',
    resendLinkWindow: '链接与就诊时间关联：在预约前两天开放，预约开始数小时后失效。',
    resendLinkConfirm: '发送链接',
    resendLinkSent: '问诊前链接已发送给 {name}。之前的链接已失效。',
    resendLinkAlreadyDone: '未发送链接：该患者已完成本次就诊的问诊前问卷。',
    resendLinkFailed: '无法发送问诊前链接。',
    assignDoctor: '指派医生 *',
    selectDoctor: '选择医生',
    appointmentDate: '预约日期 *',
    appointmentTime: '预约时间 *',
    sessionType: '就诊类型 *',
    typePhysical: '面诊',
    typeOnline: '线上',
    practitionerType: '执业类型 *',
    patientLanguage: '患者语言',
    rheumatologist: '风湿科医生',
    dermatologist: '皮肤科医生',
    generalPractitioner: '全科医生',
    viewTitle: '查看就诊详情',
    viewConfirm: '查看就诊',
    viewSubtitle: '您即将查看就诊信息',
    sessionInformation: '就诊信息',
    sessionId: '就诊编号',
    patient: '患者',
    date: '日期',
    time: '时间',
    status: '状态',
    editAppointment: '编辑预约',
    selectDateRange: '选择日期范围',
    from: '从：',
    to: '到：',
    bulkTitle: '批量上传预约',
    csvRequirements: 'CSV 文件要求：',
    csvNoHeaders: '文件必须为无表头的 CSV 格式（.csv）',
    csvMaxSize: '最大文件大小：5MB',
    dropCsv: '点击选择 CSV 文件或拖放',
    maxSizeHint: '最大文件大小：5MB',
    bulkSummary: '批量上传摘要',
    totalProcessed: '总处理数',
    successful: '成功',
    failed: '失败',
    skipped: '已跳过',
    ignored: '已忽略',
    issues: '遇到的问题：',
    downloadFailed: '下载失败记录 CSV',
    calToday: '今天',
    patientUnavailable: '患者不可用',
    unassigned: '未指派',
    notSet: '未设置',
    sessionCalendar: '就诊日历',
    colAppointmentDate: '预约日期',
    colAppointmentTime: '预约时间',
    clickToView: '点击查看就诊详情',
    editAppointmentHint: '编辑预约日期/时间',
    sessionCount: '{count} 次就诊',
    sessionCountPlural: '{count} 次就诊',
    weekdayMon: '一',
    weekdayTue: '二',
    weekdayWed: '三',
    weekdayThu: '四',
    weekdayFri: '五',
    weekdaySat: '六',
    weekdaySun: '日',
    loadFailed: '加载就诊失败。请重试。',
    navigateFailed: '无法打开就诊。请重试。',
    appointmentUpdated: '就诊 #{id} 的预约日期/时间已更新',
    updateAppointmentFailed: '更新预约失败。请重试。',
    dateRequired: '必须填写预约日期',
    datePast: '预约日期不能早于今天（新加坡时间）',
    timeRequired: '必须填写预约时间',
    nameRequired: '必须填写患者姓名',
    nameInvalid: '请输入有效姓名（2-50 个字符）',
    phoneRequired: '必须填写电话号码',
    phoneInvalid: '请输入有效电话号码',
    doctorRequired: '请选择要指派的医生',
    sessionCreated: '就诊创建成功！就诊编号：{id}',
    createFailed: '创建就诊失败。请重试。',
    statusUpdated: '预约状态已更新为“{status}”',
    statusUpdateFailed: '更新预约状态失败。请重试。',
    maxRows: '每页最多 {max} 行。',
    selectValidCsv: '请选择有效的 CSV 文件',
    fileTooLarge: '文件大小必须小于 5MB',
    selectCsv: '请选择 CSV 文件',
    uploadFailed: '上传 CSV 失败。请重试。',
    dropValidCsv: '请拖放有效的 CSV 文件',
    csvColumnOrder: '必填列顺序（15 列）：',
    downloadSample: '下载示例 CSV',
    changeFile: '更换文件',
    uploading: '正在上传...',
    uploadCsv: '上传 CSV',
    failedRecords: '失败记录（{count}）',
    skippedRecords: '跳过记录（{count}）',
    ignoredRecords: '忽略记录（{count}）',
    rowLabel: '第 {row} 行',
    unknownError: '未知错误',
    unknownReason: '未知原因',
    moreFailed: '……还有 {count} 条失败记录',
    moreSkipped: '……还有 {count} 条跳过记录',
    moreIgnored: '……还有 {count} 条忽略记录',
  },
  consult: {
    readyTitle: '准备转写',
    readyBody: '开始录音以记录本次咨询',
    consultLanguage: '咨询语言',
    transcriptionLanguage: '问诊语言',
    outputLanguage: '记录语言',
    outputLanguageHint: 'AI 笔记与分析将以记录语言撰写',
    consultLanguageLocked: '您的套餐不包含此功能',
    startRecording: '开始录音',
    uploadWav: '上传 WAV（开发）',
    uploading: '正在上传录音...',
    transcribing: '正在转写音频...',
    analyzing: '正在分析咨询...',
    complete: '完成！',
    processing: '处理中...',
    uploadingHint: '正在将音频发送到服务器',
    transcribingHint: '正在将语音转为文字',
    analyzingHint: '正在生成临床见解',
    completeHint: '即将完成',
    processingHint: '请稍候',
    initializing: '正在初始化...',
    initializingHint: '正在建立音频连接',
    recording: '录音中',
    paused: '已暂停',
    resume: '继续',
    pause: '暂停',
    stop: '停止',
    recordingPaused: '录音已暂停',
    recordingAudio: '正在录制咨询音频...',
    stopWhenFinished: '完成后请点击停止',
    stopped: '已停止',
    startNewRecording: '开始新录音',
    recordingComplete: '录音完成',
    insertHint: '点击“插入到笔记”以转写并分析',
    insertToNotes: '插入到笔记',
    consentTitle: '需要患者同意',
    consentBody:
      '在开始用于医疗转写的录音前，请确认您已获得患者的适当同意。',
    consentCheckbox:
      '我确认已就本次医疗咨询的录音和转写获得患者的适当同意。',
    acknowledge: '确认',
    startTitle: '开始录音',
    startBody: '确定开始录音吗？这将从麦克风采集音频用于转写。',
    yesStart: '是，开始录音',
    privateNotes: '私人笔记',
    saving: '（保存中...）',
    privateNotesPlaceholder: '添加关于该患者的私人笔记...',
    visitsTitle: '全部就诊时间线',
    loadingHistory: '正在加载患者病史...',
    sessionNumber: '就诊 #{id}',
    appointmentTime: '预约时间：{time}',
    noPreviousVisits: '未找到该患者的既往就诊。',
    languageUpdateFailed: '更新咨询语言失败',
    inConsultSaved: '诊中数据已保存',
    inConsultSaveFailed: '保存诊中数据失败',
    signedOff: '就诊已签核并结束。',
    endFailed: '结束就诊失败。请重试。',
    noAudio: '未录到音频',
    analysisFailed: '多次重试后分析仍失败 — 请重试',
    transcribed: '录音已转写并分析',
    processFailed: '处理录音失败',
    recovered: '已恢复的录音已转写并分析',
    recoverFailed: '恢复录音失败',
    wavDone: 'WAV 文件已转写并分析',
    wavFailed: '处理 WAV 上传失败',
    alreadySignedOff: '就诊已签核',
    alreadySignedOffBody:
      '该就诊已签核，因此药物已获批准。重新运行记录助手可能会修改或重复已批准处方。仅在您打算更新本次咨询记录时继续。',
    rerunScribe: '重新运行记录助手',
    endTitle: '结束就诊并签核？',
    endBody: '这将完成咨询并把病历推送到诊所系统。',
    endConfirm: '结束就诊',
    sectionLoading: '分析中...',
    sectionFailed: '分析失败，显示上一版记录',
    analyzingProgressive: '正在逐节生成病历...',
    analyzingProgressiveHint: '每一节分析完成后会立即显示',
    runInProgress: '记录助手分析仍在进行中，请等待所有部分完成。',
    scribeAgents: {
      history: '病史',
      examination: '体格检查',
      diagnosis: '诊断',
      medication: '用药',
      eq: '关怀提醒',
      followup: '随访',
    },
  },
  workspace: {
    tabs: {
      preClerk: '诊前',
      inConsult: '诊中',
      workflow: '流程',
      sessionSummary: '就诊摘要',
      chroniq: 'Chroniq',
      clinicalSummary: '临床摘要',
      careGaps: '诊疗缺口',
    },
    careGaps: {
      title: '诊疗缺口',
      subtitle: '基于已签署就诊记录的目标与当前疾病活动度对比',
      ruleset: '规则集 {version}',
      openCount: '{count} 项待处理',
      onTarget: '已达标',
      reevaluate: '重新评估',
      reevaluating: '评估中…',
      loading: '正在检查诊疗缺口…',
      empty: '已达标，没有待处理的诊疗缺口。',
      silent: '引擎正在静默评估。本诊所开启显示后，缺口将在此处显示。',
      showHistory: '显示 {count} 项已解决或已关闭',
      hideHistory: '隐藏已解决和已关闭',
      recorded: '已记录',
      noScore: '未记录活动度评分',
      targetNotMet: '未达标',
      targetMet: '已达标',
      targetUnknown: '目标：无法判定',
      action: '建议措施',
      due: '截止 {date}',
      closedOn: '关闭于 {date}',
      close: '关闭缺口',
      exception: '添加例外',
      rule: {
        measurementMissing: '未记录疾病活动度评分',
        aboveTarget: '疾病活动度高于目标',
      },
      disease: { ra: 'RA', psa: 'PsA', axspa: 'axSpA', sle: 'SLE' },
      status: {
        open: '待处理',
        resolved: '已解决',
        excepted: '已例外',
        closed: '已关闭',
        withdrawn: '已撤回',
      },
      dialog: {
        closeTitle: '关闭诊疗缺口',
        closeHint: '记录满足关闭标准的证据。',
        evidenceLabel: '关闭证据',
        evidencePlaceholder: '例如：今日记录 CDAI 8；已达标',
        confirmClose: '关闭缺口',
        exceptionTitle: '记录例外',
        exceptionHint: '个体化目标或接受当前活动度的书面理由。在例外到期前，该缺口将被抑制。',
        rationaleLabel: '理由',
        rationalePlaceholder: '例如：妊娠期暂停达标治疗；20 周时复查',
        expiresLabel: '复查截止',
        expiresHint: '必须是未来日期，最多 12 个月。',
        confirmException: '保存例外',
        cancel: '取消',
        saving: '保存中…',
      },
      errors: {
        displayOff: '本诊所尚未开启诊疗缺口功能。',
        evidenceRequired: '必须填写关闭证据。',
        rationaleRequired: '必须填写理由。',
        notFound: '该诊疗缺口已不再处于待处理状态。',
        invalidException: '例外必须在 12 个月内的未来日期到期。',
        generic: '无法更新诊疗缺口，请重试。',
      },
    },
    subTabs: {
      history: '病史 (Hx)',
      exam: '检查',
      dx: '诊断/评估',
      plan: '计划 / Mx',
      billing: '计费与医嘱',
      pro: 'PRO',
      aiScribe: 'AI 记录',
    },
    headings: {
      practitionerNotes: '医师笔记',
      practitionerNotesEmpty: '暂无医师笔记。点击添加。',
      practitionerNotesPlaceholder: '点击添加笔记...',
      hpi: '现病史',
      hpiPlaceholder: '输入现病史...',
      hpiEmpty: '暂无现病史。点击添加。',
      ros: '系统回顾',
      rosPlaceholder: '输入系统回顾（例如：全身：否认发热...）',
      rosEmpty: '暂无系统回顾。点击添加。',
      pastMedicalHx: '既往病史',
      pastMedicalHxPlaceholder: '输入既往病史...',
      pastMedicalHxEmpty: '暂无既往病史。点击添加。',
      pastSurgicalHx: '既往手术史',
      pastSurgicalHxPlaceholder: '输入既往手术史...',
      pastSurgicalHxEmpty: '暂无既往手术史。点击添加。',
      familyHistory: '家族史',
      familyHistoryAdd: '添加',
      familyHistoryAddTitle: '添加家庭成员',
      familyHistoryEmpty: '暂无家族史',
      familyHistoryEmptyHint: '先将患者（先证者）加入家系图。',
      familyHistoryStart: '开始家系图',
      familyHistoryExpanded: '家族史 - 展开视图',
      familyHistoryMale: '男性',
      familyHistoryFemale: '女性',
      familyHistoryAffected: '患病',
      familyHistoryDeceased: '已故',
      medsAllergies: '用药与过敏',
      medications: '用药：',
      medicationsPlaceholder: '输入当前用药...',
      medicationsEmpty: '暂无用药记录。点击添加。',
      allergies: '过敏：',
      allergiesPlaceholder: '输入过敏史...',
      allergiesEmpty: '暂无过敏记录。点击添加。',
      socialHistory: '社会史',
      socialHistoryPlaceholder: '输入社会史...',
      socialHistoryEmpty: '暂无社会史。点击添加。',
      historySection: '病史',
      rosPlaceholderBrief: '输入系统回顾...',
      hpiEmptyBrief: '暂无现病史。',
      rosEmptyBrief: '暂无系统回顾。',
      pastMedicalHxEmptyBrief: '暂无既往病史。',
      pastSurgicalHxEmptyBrief: '暂无既往手术史。',
      medicationsEmptyBrief: '暂无用药记录。',
      socialHistoryEmptyBrief: '暂无社会史。',
      copyToClipboard: '复制到剪贴板',
    },
    left: {
      mrn: '病历号',
      noMrn: '暂无病历号',
      noName: '暂无姓名',
      ageGender: '年龄 / 性别',
      lastVisit: '上次就诊',
      noPreviousVisits: '无既往就诊',
      privateNote: '私人笔记',
      privateNotePlaceholder: '添加临床背景...',
      activeProblems: '当前问题',
      noActiveProblems: '暂无当前问题',
      activeMedications: '当前用药',
      noActiveMedications: '暂无当前用药',
      lastInvestigation: '最近检查',
      noData: '暂无数据',
      moreOptions: '更多选项',
      edit: '编辑',
    },
    alerts: {
      title: '过敏与警示',
      drugAllergies: '药物过敏',
      otherAlerts: '其他警示',
      noAllergyRecord: '尚无过敏记录。',
      noKnownDrugAllergies: '无已知药物过敏',
      addAllergy: '添加过敏',
      addAlert: '添加警示',
      allergyPlaceholder: '药物及反应，例如：青霉素 - 皮疹',
      alertPlaceholder: '警示内容',
      medicalAlert: '医疗警示',
      anticoagulation: '抗凝',
      fromChat: '来自诊前对话',
      recordedByDoctor: '由医生记录',
      edit: '编辑',
      remove: '删除',
      confirmRemove: '删除此条目？',
      loadFailed: '无法加载过敏与警示。',
      saveFailed: '无法保存：{reason}',
    },
    right: {
      timeline: '时间线',
      rx: '处方',
      review: '审核',
      ix: '检验',
      orders: '医嘱',
      quickRx: '快速处方',
      letters: '文书',
      visitTimeline: '就诊时间线',
      loadingHistory: '正在加载患者病史...',
      noPreviousVisits: '未找到既往就诊。',
      sessionLabel: '就诊 #{id} - {name}',
      patientFallback: '患者',
      currentlyViewing: '正在查看',
      didNotAttend: '未到诊',
      completed: '已完成',
      closed: '已关闭',
      consultationComplete: '咨询已完成',
      inProgress: '进行中',
    },
    preclerk: {
      patientHistory: '患者病史与症状（AI 生成）',
      noPatientHistory: '暂无患者病史',
      ros: '系统回顾：',
      noRos: '暂无系统回顾',
      pastMedical: '既往医疗问题：',
      noPastMedical: '暂无既往医疗问题',
      pastSurgical: '既往手术问题：',
      noPastSurgical: '暂无既往手术问题',
      familyHistory: '家族史：',
      noFamilyHistory: '暂无家族史',
      drugAllergies: '药物与过敏：',
      noDrugAllergies: '暂无药物或过敏',
      socialHistory: '社会史：',
      noSocialHistory: '暂无社会史',
    },
    exam: {
      msk: '肌骨',
      general: '一般',
      skinHeent: '皮肤/五官',
      eyes: '眼',
      cvs: '心血管',
      respiratory: '呼吸',
      abdominal: '腹部',
      neuro: '神经',
      summary: '摘要',
      skinExam: '皮肤检查',
      generalAppearance: '一般情况',
      skinMucocutaneous: '皮肤与黏膜',
      examinerSummary: '检查者摘要与印象',
      diseaseActivity: '疾病活动度评分与 PRO',
      cardiovascular: '心血管',
      jointExam: '肌肉骨骼 / 关节检查',
      neurological: '神经系统',
      eyesSection: '眼',
      respiratorySection: '呼吸系统',
      abdominalSection: '腹部',
      lymphoreticular: '淋巴网状与唾液腺',
    },
    dx: {
      section: '诊断 / 评估',
      primary: '主要诊断',
      principal: '主要问题',
      acute: '急性诊断',
      chronic: '慢性诊断',
      differential: '考虑的鉴别诊断',
      clinicalReasoning: '临床推理',
      addDiagnosis: '添加诊断',
      addAcute: '添加急性诊断',
      addChronic: '添加慢性诊断',
      addDifferential: '添加鉴别诊断',
      noPrimary: '暂无主要诊断',
      noAcute: '暂无急性诊断',
      noChronic: '暂无慢性诊断',
      noDifferential: '暂无考虑的鉴别诊断',
      insights: '指南临床见解',
      insightsUpper: '指南临床见解',
    },
    ehrChat: {
      title: '与 EHR 对话',
      ready: 'AI 助手已就绪',
      unavailable: '不可用',
      initializing: '正在初始化...',
      close: '关闭对话',
      connecting: '正在连接 EHR...',
      medicationHistory: '用药史',
      medicationHistoryQuery: '显示用药史',
      recentLabs: '近期检验',
      recentLabsQuery: '总结近期检验',
      previousVisits: '既往就诊',
      previousVisitsQuery: '显示既往就诊',
      placeholder: '询问患者数据、用药、结果...',
      send: '发送消息',
    },
  },
};
